import { Breakpoints } from '@mui/system';
import contentTokens from '../../../design-tokens/src/tokens/typography/decisions/typography-content.json';
import formActionTokens from '../../../design-tokens/src/tokens/typography/decisions/typography-form-action.json';
import titleTokens from '../../../design-tokens/src/tokens/typography/decisions/typography-title.json';
import keysToCamelCase from '../../../design-tokens/src/helpers/keysToCamelCase';
import { omitDeep } from './omitDeep';

type VariantBaseStyle = {
  fontSize: string;
  letterSpacing: string;
  lineHeight: string;
  fontWeight: number;
  textDecoration: string;
  textTransform: string;
};

export type DesktopVariantStyle = VariantBaseStyle & {
  [mediaQuery in `@media ${string}`]?: VariantBaseStyle;
};

export type VariantStyle = VariantBaseStyle;

type TypographyBase = {
  fontFamily: string;
  fontWeightRegular: number;
  fontWeightMedium: number;
  fontWeightBold: number;
};

// Full set of typography variants in theme
type TypographyVariantKey =
  | 'body'
  | 'bodyHighlight'
  | 'bodySmall'
  | 'caption'
  | 'overline'
  | 'subheading'
  | 'subheadingSmall'
  | 'highlight'
  | 'medium'
  | 'checkbox'
  | 'checkboxSingle'
  | 'chip'
  | 'errorMessage'
  | 'helper'
  | 'input'
  | 'label'
  | 'radioButton'
  | 'h1'
  | 'h1Impact'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'button'
  | 'hyperlink'
  | 'hyperlinkLarge'
  | 'hyperlinkSmall'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2';

const desktopVariantKeys = [
  'highlight',
  'medium',
  'h1',
  'h1Impact',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const;

// Variants having the desktop media-query value added for a breakpoint override
export type DesktopVariantKey = (typeof desktopVariantKeys)[number];
type NonDesktopVariantKey = Exclude<TypographyVariantKey, DesktopVariantKey>;

type TypographyVariants = Record<NonDesktopVariantKey, VariantStyle> &
  Record<DesktopVariantKey, DesktopVariantStyle>;

type TypographyTheme = TypographyBase & TypographyVariants;

type TypographyDecisionMap = Record<string, { value: VariantBaseStyle }>;

type TypographyTokenGroups = {
  content: typeof contentTokens.content;
  contentImpact: (typeof contentTokens)['content-impact'];
  contentImpactMobile: (typeof contentTokens)['content-impact-mobile'];
  action: typeof formActionTokens.action;
  form: typeof formActionTokens.form;
  title: typeof titleTokens.title;
  titleMobile: (typeof titleTokens)['title-mobile'];
};

const tokenGroups = {
  content: contentTokens.content,
  contentImpact: contentTokens['content-impact'],
  contentImpactMobile: contentTokens['content-impact-mobile'],
  action: formActionTokens.action,
  form: formActionTokens.form,
  title: titleTokens.title,
  titleMobile: titleTokens['title-mobile'],
} satisfies TypographyTokenGroups;

const extractVariantStyles = (decisions: TypographyDecisionMap) => {
  const camelCasedDecisions = keysToCamelCase(decisions) as TypographyDecisionMap;

  return Object.fromEntries(
    Object.entries(camelCasedDecisions).map(([key, decision]) => [key, decision.value])
  ) as Record<string, VariantStyle>;
};

const removeFontFamilyFromTypography = <T>(value: T): T => omitDeep('fontFamily')(value) as T;

const getTypography = ({ breakpoints }: { breakpoints: Breakpoints }): TypographyTheme => {
  const cleanTypography = removeFontFamilyFromTypography({
    content: extractVariantStyles(tokenGroups.content),
    contentImpact: extractVariantStyles(tokenGroups.contentImpact),
    contentImpactMobile: extractVariantStyles(tokenGroups.contentImpactMobile),
    action: extractVariantStyles(tokenGroups.action),
    form: extractVariantStyles(tokenGroups.form),
    title: extractVariantStyles(tokenGroups.title),
    titleMobile: extractVariantStyles(tokenGroups.titleMobile),
  });

  const fontDefault: TypographyBase = {
    fontFamily: '"Inter", "inter-fallback", Arial, system-ui, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  };

  const variants: TypographyVariants = {
    body: cleanTypography.content.body,
    bodyHighlight: cleanTypography.content.bodyHighlight,
    bodySmall: cleanTypography.content.bodySmall,
    caption: cleanTypography.content.caption,
    overline: cleanTypography.content.overline,
    subheading: cleanTypography.content.subheading,
    subheadingSmall: cleanTypography.content.subheadingSmall,
    highlight: cleanTypography.contentImpactMobile.highlight,
    medium: cleanTypography.contentImpactMobile.medium,
    checkbox: cleanTypography.form.checkbox,
    checkboxSingle: cleanTypography.form.checkboxSingle,
    chip: cleanTypography.form.chip,
    errorMessage: cleanTypography.form.errorMessage,
    helper: cleanTypography.form.helper,
    input: cleanTypography.form.input,
    label: cleanTypography.form.label,
    radioButton: cleanTypography.form.radioButton,
    h1: cleanTypography.titleMobile.h1,
    h1Impact: cleanTypography.titleMobile.h1Impact,
    h2: cleanTypography.titleMobile.h2,
    h3: cleanTypography.titleMobile.h3,
    h4: cleanTypography.titleMobile.h4,
    h5: cleanTypography.titleMobile.h5,
    h6: cleanTypography.titleMobile.h6,
    button: cleanTypography.action.button,
    hyperlink: cleanTypography.action.hyperlink,
    hyperlinkLarge: cleanTypography.action.hyperlinkLarge,
    hyperlinkSmall: cleanTypography.action.hyperlinkSmall,
    subtitle1: cleanTypography.content.subheading,
    subtitle2: cleanTypography.content.subheadingSmall,
    body1: cleanTypography.content.body,
    body2: cleanTypography.content.bodySmall,
  };

  // Todo : Should be added in figma
  variants.button.textTransform = 'Initial';

  const desktopVariants: Record<DesktopVariantKey, VariantStyle> = {
    highlight: cleanTypography.contentImpact.highlight,
    medium: cleanTypography.contentImpact.medium,
    h1: cleanTypography.title.h1,
    h1Impact: cleanTypography.title.h1Impact,
    h2: cleanTypography.title.h2,
    h3: cleanTypography.title.h3,
    h4: cleanTypography.title.h4,
    h5: cleanTypography.title.h5,
    h6: cleanTypography.title.h6,
  };

  const desktopMediaQuery = breakpoints.up('sm') as `@media ${string}`;

  desktopVariantKeys.forEach((variantName) => {
    variants[variantName][desktopMediaQuery] = desktopVariants[variantName];
  });
  return { ...variants, ...fontDefault };
};

export default getTypography;
